import '../../css/PanelParallelCoord.less';

import React from 'react';
import _ from 'lodash';

import {scaleBand, scaleLinear} from '@vx/scale';
import {Group} from '@vx/group';
import {AxisLeft, AxisBottom} from '@vx/axis';
import * as Shape from '@vx/shape';
import {useTooltip, TooltipWithBounds} from '@vx/tooltip';
import {localPoint} from '@vx/event';
import * as Stats from '@vx/stats';
import {
  Bar,
  barOverlay,
  getPlotMargin,
  axisFontStyles,
  axisTickRotate,
  getAngledXAxisMarginHeight,
} from '../../util/plotHelpers';
import {ParentSize} from '@vx/responsive';
import makeComp from '../../util/profiler';

interface Margin {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface BarChartProps {
  bars: Bar[];
  vertical?: boolean;
  height?: number;
  min?: number;
  max?: number;
  maxBars?: number;
  boxPlot?: boolean;
  violinPlot?: boolean;
  highlight?: Bar;
  showAllLabels?: boolean;
  mouseOver?: (
    event: React.MouseEvent<SVGRectElement, MouseEvent>,
    bar: Bar
  ) => void;
  mouseOut?: () => void;
}

type BarChartVizProps = BarChartProps & {
  margin: Margin;
  xDomain: number[];
  yDomain: number[];
};

// responsive utils for axis ticks
function numTicksForHeight(h: number) {
  if (h <= 300) {
    return 3;
  }
  if (300 < h && h <= 600) {
    return 5;
  }
  return 10;
}

function numTicksForWidth(w: number) {
  if (w <= 300) {
    return 3;
  }
  if (300 < w && w <= 400) {
    return 5;
  }
  return 10;
}

const BarChart = makeComp(
  (props: BarChartProps) => {
    const {mouseOver, mouseOut, maxBars} = props;

    const bars = maxBars != null ? props.bars.slice(0, maxBars) : props.bars;

    const {
      tooltipData,
      tooltipLeft,
      tooltipTop,
      tooltipOpen,
      showTooltip,
      hideTooltip,
    } = useTooltip();

    const yDomain = bars.map((d, i) => i + 1);

    const xDomain = [
      props.min ??
        Math.min(...bars.map(d => (isNaN(d.value) ? 0 : d.value)), 0),
      props.max ??
        Math.max(...bars.map(d => (isNaN(d.value) ? 0 : d.value)), 0),
    ];

    const handleMouseOver = (
      event: React.MouseEvent<SVGRectElement, MouseEvent>,
      bar: Bar
    ) => {
      const coords = localPoint(event);
      showTooltip({
        tooltipLeft: coords?.x ?? 0,
        tooltipTop: coords?.y ?? 0,
        tooltipData: bar,
      });
      if (mouseOver != null) {
        mouseOver(event, bar);
      }
    };

    const handleMouseOut = () => {
      hideTooltip();
      if (mouseOut != null) {
        mouseOut();
      }
    };

    const tooltip = (
      <TooltipWithBounds
        // set this to random so it correctly updates with parent bounds key
        key={Math.random()}
        className={'rv-hint'}
        top={tooltipTop}
        left={tooltipLeft}
        style={{backgroundColor: 'transparent'}}>
        <div className={'rv-hint__content'}>
          {tooltipData != null && (tooltipData as Bar).hasOwnProperty('key') ? (
            barOverlay(tooltipData as Bar)
          ) : (
            <></>
          )}
        </div>
      </TooltipWithBounds>
    );

    const vals = _.flatten(
      bars.map(b =>
        b.bins != null
          ? b.bins.map(d => d.bin)
          : b.quartiles != null
          ? b.quartiles
          : [b.value]
      )
    );

    const min = props.min ?? Math.min(...vals.map(v => (isNaN(v) ? 0 : v)), 0);
    const max = props.max ?? Math.max(...vals.map(v => (isNaN(v) ? 0 : v)), 0);

    const margin = getPlotMargin(
      {},
      {yAxis: yDomain},
      {
        yAxis: 'linear',
      }
    );

    const fillStyle = React.useMemo(() => {
      return props.height != null ? {height: props.height} : {};
    }, [props.height]);

    if (props.vertical) {
      return (
        <div className="fill" style={fillStyle}>
          <ParentSize>
            {parent => (
              <VerticalBarChart
                {...{
                  ...props,
                  bars,
                  width: parent.width,
                  height: parent.height,
                  margin,
                  mouseOver: handleMouseOver,
                  mouseOut: handleMouseOut,
                  min,
                  max,
                  xDomain,
                  yDomain,
                }}
              />
            )}
          </ParentSize>
          {tooltipOpen && tooltip}
        </div>
      );
    } else {
      return (
        <div className="fill" style={fillStyle}>
          <ParentSize>
            {parent => (
              <HorizontalBarChart
                {...{
                  ...props,
                  bars,
                  height: parent.height,
                  width: parent.width,
                  margin,
                  mouseOver: handleMouseOver,
                  mouseOut: handleMouseOut,
                  min,
                  max,
                  xDomain,
                  yDomain,
                }}
              />
            )}
          </ParentSize>
          {tooltipOpen && tooltip}
        </div>
      );
    }
  },
  {id: 'BarChart'}
);

export default BarChart;

const HorizontalBarChart = makeComp(
  (props: BarChartVizProps & {width: number; height: number}) => {
    const {
      bars,
      width,
      height,
      mouseOver,
      mouseOut,
      boxPlot,
      violinPlot,
      yDomain,
    } = props;

    const xDomain = [
      props.min ?? 0,
      props.max ?? Math.max(...bars.map(d => (isNaN(d.value) ? 0 : d.value))),
    ];

    const yKeys = bars.map(d => d.key);

    const margin = getPlotMargin(
      {},
      {yAxis: yDomain},
      {
        yAxis: 'linear',
      },
      {yAxis: yKeys}
    );

    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    const yScale = scaleBand({
      rangeRound: [0, yMax],
      domain: yDomain,
      padding: 0.25,
    });

    const xScale = scaleLinear({
      rangeRound: [0, xMax],
      domain: xDomain,
    }) as d3.ScaleLinear<number, number>; // seems like a bug in vx typing

    return (
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left} key="chart">
          {bars.map((d, i) => {
            const barHeight = Math.max(0, yScale.bandwidth());
            const barY = yScale(i + 1) ?? 0;
            const constrainedHeight = Math.min(40, barHeight);
            const key = d.key + i.toString();

            if (violinPlot) {
              return (
                <Stats.ViolinPlot
                  key={key}
                  data={d.bins ?? []}
                  count={data => data.count}
                  value={data => data.bin}
                  top={barY + barHeight / 2 - constrainedHeight / 2}
                  width={constrainedHeight} // not a bug - here width is "bar" width
                  valueScale={xScale}
                  fill={d.color ?? 'red'}
                  fillOpacity={0.5}
                  stroke={d.color ?? 'red'}
                  strokeWidth={1}
                  horizontal
                />
              );
            } else if (boxPlot) {
              return (
                <Stats.BoxPlot
                  key={key}
                  min={d.quartiles != null ? d.quartiles[0] : d.value}
                  max={d.quartiles != null ? d.quartiles[4] : d.value}
                  median={d.quartiles != null ? d.quartiles[2] : d.value}
                  firstQuartile={d.quartiles != null ? d.quartiles[1] : d.value}
                  thirdQuartile={d.quartiles != null ? d.quartiles[3] : d.value}
                  top={barY + barHeight / 2 - constrainedHeight / 2}
                  fill={d.color ?? 'red'}
                  fillOpacity={0.5}
                  stroke={d.color ?? 'red'}
                  strokeWidth={1}
                  boxWidth={constrainedHeight}
                  valueScale={xScale}
                  horizontal
                  boxProps={{
                    onMouseOver: event => mouseOver && mouseOver(event, d),
                    onMouseLeave: event => mouseOut && mouseOut(),
                  }}
                />
              );
            } else {
              let barX = Math.max(xScale(0), xScale(xDomain[0]), 0);
              let barWidth = xScale(d.value) - barX;
              if (isNaN(barWidth)) {
                barWidth = 0;
              }
              if (barWidth < 0) {
                barWidth = Math.abs(barWidth);
                barX = barX - barWidth;
                if (barX < 0) {
                  barWidth = Math.max(barWidth + barX, 0);
                  barX = 0;
                }
              }

              return (
                <Group key={key}>
                  <Shape.Bar
                    key={key}
                    width={barWidth}
                    height={barHeight}
                    x={barX}
                    y={barY}
                    fill={d.color ?? 'red'}
                    onMouseOver={event => mouseOver && mouseOver(event, d)}
                    onMouseOut={event => mouseOut && mouseOut()}
                  />
                  {d.range != null &&
                    _.isFinite(d.range[0]) &&
                    _.isFinite(d.range[1]) && (
                      <Shape.Bar
                        key={key + 'error-bar'}
                        width={Math.max(
                          0,
                          xScale(d.range[1]) - xScale(d.range[0])
                        )}
                        height={1}
                        x={xScale(d.range[0])}
                        y={(barY ?? 0) + barHeight / 2}
                        fill={'black'}
                      />
                    )}
                </Group>
              );
            }
          })}
          {xDomain[0] <= 0 && (
            // origin line
            <Shape.Line
              x1={xScale(0)}
              y1={0}
              x2={xScale(0)}
              y2={yMax}
              stroke="#aaa"
              strokeWidth={1}
            />
          )}
        </Group>
        <Group key="axis">
          <AxisLeft
            top={margin.top}
            left={margin.left}
            scale={yScale}
            hideZero
            numTicks={numTicksForHeight(height)}
            labelProps={{
              ...axisFontStyles,
              textAnchor: 'middle',
            }}
            stroke="#aaa"
            tickStroke="#b3b3b0"
            strokeWidth={0.5}
            tickLabelProps={(value: any, index: any) => ({
              ...axisFontStyles,
              textAnchor: 'end',
              dx: '-0.25em',
              dy: '0.25em',
            })}
            tickComponent={({formattedValue, ...tickProps}) => (
              <text {...(tickProps as any)}>
                {bars[parseInt((formattedValue ?? '0').toString(), 10) - 1].key}
              </text>
            )}
          />
          <line
            x1={margin.left}
            y1={height - margin.bottom}
            x2={width - margin.right}
            y2={height - margin.bottom}
            stroke="#888"
            strokeWidth={0.5}
          />

          <AxisBottom
            top={height - margin.bottom}
            left={margin.left}
            scale={xScale}
            stroke="#aaa"
            strokeWidth={0.5}
            tickStroke="#b3b3b0"
            labelProps={{
              ...axisFontStyles,
              textAnchor: 'middle',
            }}
            tickLabelProps={(value: any, index: any) => ({
              ...axisFontStyles,
              textAnchor: 'middle',
              dx: '-0.25em',
              dy: '0.25em',
            })}
            numTicks={numTicksForWidth(width)}></AxisBottom>
        </Group>
      </svg>
    );
  },
  {id: 'HorizontalBarChart'}
);

const VerticalBarChart = makeComp(
  (props: BarChartVizProps & {width: number; height: number}) => {
    const {
      bars,
      width,
      height,
      mouseOver,
      mouseOut,
      boxPlot,
      violinPlot,
    } = props;

    const yDomain = [
      props.min ?? 0,
      props.max ?? Math.max(...bars.map(d => (isNaN(d.value) ? 0 : d.value))),
    ];

    const xKeys = bars.map(d => d.key);
    const margin = getPlotMargin(
      {},
      {yAxis: yDomain},
      {
        yAxis: 'linear',
      }
    );

    const xMargin = getAngledXAxisMarginHeight(xKeys);
    margin.bottom = xMargin;

    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    const xScale = scaleBand({
      rangeRound: [0, xMax],
      domain: bars.map((d, i) => i + 1),
      padding: 0.25,
    });
    const yScale = scaleLinear({
      rangeRound: [yMax, 0],
      domain: yDomain,
    }) as d3.ScaleLinear<number, number>; // seems like a bug in vx typing
    return (
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left} key="chart">
          {bars.map((d, i) => {
            const barWidth = Math.max(0, xScale.bandwidth());
            const constrainedWidth = Math.min(40, barWidth);

            const barX = xScale(i + 1) ?? 0;
            const key = d.key + i.toString();
            if (violinPlot) {
              return (
                <Stats.ViolinPlot
                  key={key}
                  data={d.bins ?? []}
                  count={data => data.count}
                  value={data => data.bin}
                  left={barX + barWidth / 2 - constrainedWidth / 2}
                  width={constrainedWidth}
                  valueScale={yScale}
                  fill={d.color ?? 'red'}
                  fillOpacity={0.5}
                  stroke={d.color ?? 'red'}
                  strokeWidth={1}
                />
              );
            } else if (boxPlot) {
              return (
                <Stats.BoxPlot
                  key={key}
                  min={d.quartiles != null ? d.quartiles[0] : d.value}
                  max={d.quartiles != null ? d.quartiles[4] : d.value}
                  median={d.quartiles != null ? d.quartiles[2] : d.value}
                  firstQuartile={d.quartiles != null ? d.quartiles[1] : d.value}
                  thirdQuartile={d.quartiles != null ? d.quartiles[3] : d.value}
                  left={barX + barWidth / 2 - constrainedWidth / 2}
                  fill={d.color ?? 'red'}
                  fillOpacity={0.5}
                  stroke={d.color ?? 'red'}
                  strokeWidth={1}
                  valueScale={yScale}
                  boxWidth={constrainedWidth}
                  boxProps={{
                    onMouseOver: event => mouseOver && mouseOver(event, d),
                    onMouseLeave: event => mouseOut && mouseOut(),
                  }}
                />
              );
            } else {
              // barY is where the bar starts, either the origin or the minimum y value
              let barY = Math.max(0, Math.min(yScale(0), yScale(yDomain[0])));
              let barHeight = yScale(d.value) - barY;

              if (isNaN(barHeight)) {
                barHeight = 0;
              }
              if (barHeight < 0) {
                barHeight = Math.abs(barHeight);
                barY = barY - barHeight;
                if (barY < 0) {
                  barHeight = Math.max(barHeight + barY, 0);
                  barY = 0;
                }
              }

              return (
                <Group key={key}>
                  <Shape.Bar
                    key={key}
                    width={barWidth}
                    height={barHeight}
                    x={barX}
                    y={barY}
                    fill={d.color ?? 'red'}
                    onMouseOver={event => mouseOver && mouseOver(event, d)}
                    onMouseOut={event => mouseOut && mouseOut()}
                  />
                  {d.range != null &&
                    _.isFinite(d.range[0]) &&
                    _.isFinite(d.range[1]) && (
                      <Shape.Bar
                        key={key + 'error-bar'}
                        height={Math.max(
                          0,
                          yScale(d.range[0]) - yScale(d.range[1])
                        )}
                        width={1}
                        y={yScale(d.range[1])}
                        x={(barX ?? 0) + barWidth / 2}
                        fill={'black'}
                      />
                    )}
                </Group>
              );
            }
          })}
          {yDomain[0] <= 0 && (
            // origin line
            <Shape.Line
              x1={0}
              y1={yScale(0)}
              x2={xMax}
              y2={yScale(0)}
              stroke="#aaa"
              strokeWidth={1.0}
            />
          )}
        </Group>
        <Group key="axis">
          <AxisLeft
            top={margin.top}
            left={margin.left}
            scale={yScale}
            numTicks={numTicksForHeight(height)}
            labelProps={{
              ...axisFontStyles,
              textAnchor: 'middle',
            }}
            stroke="#aaa"
            tickStroke="#b3b3b0"
            strokeWidth={0.5}
            tickLabelProps={(value: any, index: any) => ({
              ...axisFontStyles,
              textAnchor: 'end',
              dx: '-0.25em',
              dy: '0.25em',
            })}
            tickComponent={({formattedValue, ...tickProps}) => {
              return <text {...(tickProps as any)}>{formattedValue}</text>;
            }}
          />

          <line
            x1={margin.left}
            y1={height - margin.bottom}
            x2={width - margin.right}
            y2={height - margin.bottom}
            stroke="#aaa"
            strokeWidth={0.5}
          />

          <AxisBottom
            top={height - margin.bottom}
            left={margin.left}
            scale={xScale}>
            {axis => {
              const tickLabelSize = 8;
              const tickRotate = axisTickRotate;
              const tickColor = '#aaa';
              const axisCenter =
                (axis.axisToPoint.x - axis.axisFromPoint.x) / 2;
              const numTicks = numTicksForWidth(width);
              return (
                <g className="my-custom-bottom-axis">
                  {axis.ticks
                    .filter(
                      (tick, i) =>
                        props.showAllLabels ||
                        axis.ticks.length <= numTicks ||
                        i % Math.floor(axis.ticks.length / (numTicks - 1)) ===
                          0 ||
                        i === 0 ||
                        i === axis.ticks.length - 1
                    )
                    .map((tick, i) => {
                      const tickX = tick.to.x;
                      const tickY =
                        tick.to.y + tickLabelSize + axis.tickLength - 10;
                      return (
                        <Group
                          key={`vx-tick-${tick.value}-${i}`}
                          className={'vx-axis-tick'}>
                          <Shape.Line
                            from={tick.from}
                            to={tick.to}
                            stroke={tickColor}
                          />
                          <text
                            transform={`translate(${tickX}, ${tickY}) rotate(${tickRotate})`}
                            fontFamily={axisFontStyles.fontFamily}
                            fontSize={tickLabelSize}
                            textAnchor="end"
                            fill={'#6b6b76'}>
                            {
                              // Here the formattedValue is acutally the index of the tick
                              // If we just use the text, when two values are the same, the
                              // bars are put on top of each other.
                              bars[
                                parseInt(
                                  (tick.formattedValue ?? '0').toString(),
                                  10
                                ) - 1
                              ].key
                            }
                          </text>
                        </Group>
                      );
                    })}
                  <text
                    textAnchor="middle"
                    transform={`translate(${axisCenter}, 20)`}
                    fontSize="8">
                    {axis.label}
                  </text>
                </g>
              );
            }}
          </AxisBottom>
        </Group>
      </svg>
    );
  },
  {id: 'VerticalBarChart'}
);
